<script setup lang="ts">
import { computed } from 'vue';

import type { PlanTask } from '@/types/domain';

const props = defineProps<{ tasks: PlanTask[] }>();

const emit = defineEmits<{
  /** 点某一格 → 把下面的表筛到那一类，图表不能只是好看 */
  drill: [filter: { kind: 'gap' | 'status'; value: string }];
}>();

const TERMINAL = new Set(['completed', 'approved', 'cancelled', 'not_applicable']);

const STATUS_LABEL: Readonly<Record<string, string>> = {
  pending: '待处理', in_progress: '进行中', blocked: '受阻', completed: '已完成',
  approved: '已批准', rejected: '已退回', not_applicable: '不适用',
};

/** 只统计适用的节点：标了「不适用」的本来就不用维护日期和责任人 */
const applicable = computed(() => props.tasks.filter((task) => task.applicable !== false));

function isDone(task: PlanTask): boolean {
  return TERMINAL.has(task.status) || Boolean(task.actualEnd);
}

function hasOwner(task: PlanTask): boolean {
  return Boolean(task.ownerUserId || task.ownerName || task.ownerRole);
}

function needsDates(task: PlanTask): boolean {
  return !task.planStart || !task.planEnd;
}

const total = computed(() => props.tasks.length);
const noDates = computed(() => applicable.value.filter(needsDates).length);
const noOwner = computed(() => applicable.value.filter((task) => !hasOwner(task)).length);
const overdue = computed(() => applicable.value.filter((task) => task.overdue && !isDone(task)).length);
const done = computed(() => applicable.value.filter(isDone).length);

const percent = computed(() => (applicable.value.length
  ? Math.round((done.value / applicable.value.length) * 100)
  : 0));

interface Slice { label: string; value: string; count: number; tone?: 'risk' | 'warning' | 'normal' }

/**
 * 维护缺口。
 *
 * 这一页最常见的画面就是满屏「日期未维护」「未分配」——
 * 先告诉人还差多少、差在哪，比按状态分布更有用。
 */
const gaps = computed<Slice[]>(() => ([
  { label: '缺计划日期', value: 'no-dates', count: noDates.value, tone: 'risk' as const },
  { label: '未分配责任人', value: 'no-owner', count: noOwner.value, tone: 'warning' as const },
  { label: '已逾期未完成', value: 'overdue', count: overdue.value, tone: 'risk' as const },
  {
    label: '关键交付物待审批',
    value: 'awaiting-approval',
    count: applicable.value.filter((task) =>
      task.keyDeliverable && task.approvalRequired && task.approvalStatus !== 'approved').length,
    tone: 'warning' as const,
  },
] satisfies Slice[]).filter((item) => item.count > 0));

const byStatus = computed<Slice[]>(() => {
  const map = new Map<string, number>();
  for (const task of props.tasks) {
    const key = task.status || 'pending';
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([key, count]) => ({
      label: STATUS_LABEL[key] ?? key,
      value: key,
      count,
      tone: TERMINAL.has(key) ? 'normal' as const : 'warning' as const,
    }))
    .sort((a, b) => b.count - a.count);
});

const maxOf = (slices: Slice[]): number => Math.max(1, ...slices.map((item) => item.count));
</script>

<template>
  <section class="ap-dashboard" aria-label="阶段工作维护情况">
    <div class="ap-kpis">
      <article>
        <b>{{ total }}</b><span>节点总数</span>
      </article>
      <article>
        <b>{{ percent }}%</b>
        <span>{{ done }} / {{ applicable.length }} 已完成</span>
      </article>
      <article :class="{ risk: noDates > 0 }">
        <b>{{ noDates }}</b><span>缺计划日期</span>
      </article>
      <article :class="{ warning: noOwner > 0 }">
        <b>{{ noOwner }}</b><span>未分配责任人</span>
      </article>
      <article :class="{ risk: overdue > 0 }">
        <b>{{ overdue }}</b><span>已逾期未完成</span>
      </article>
    </div>

    <!--
      图表默认折叠（1027 审查 P2-10）：这一页是来改节点的，清单该先露出来。
      上面五个读数够判断要不要往下看；要看分布、点格子下钻再展开。
    -->
    <details class="ap-charts-fold">
      <summary>查看状态与缺口分析</summary>
      <div class="ap-charts">
        <article v-if="gaps.length">
          <h4>待补的维护缺口</h4>
          <p class="ap-note">只统计「适用」的节点；标了不适用的本来就不用维护。</p>
          <ul>
            <li v-for="slice in gaps" :key="slice.value">
              <button type="button" @click="emit('drill', { kind: 'gap', value: slice.value })">
                <span class="ap-label">{{ slice.label }}</span>
                <span class="ap-track">
                  <i :class="slice.tone" :style="{ width: `${Math.round((slice.count / maxOf(gaps)) * 100)}%` }"></i>
                </span>
                <b>{{ slice.count }}</b>
              </button>
            </li>
          </ul>
        </article>
        <article v-else class="ap-clean">
          <h4>待补的维护缺口</h4>
          <p class="ap-note">这个范围内没有缺日期、缺责任人或逾期的节点。</p>
        </article>

        <article>
          <h4>按状态</h4>
          <ul>
            <li v-for="slice in byStatus" :key="slice.value">
              <button type="button" @click="emit('drill', { kind: 'status', value: slice.value })">
                <span class="ap-label">{{ slice.label }}</span>
                <span class="ap-track">
                  <i :class="slice.tone" :style="{ width: `${Math.round((slice.count / maxOf(byStatus)) * 100)}%` }"></i>
                </span>
                <b>{{ slice.count }}</b>
              </button>
            </li>
          </ul>
        </article>
      </div>
    </details>
  </section>
</template>

<style scoped>
.ap-dashboard { display: grid; gap: 12px; margin: 14px 0; }
.ap-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(132px, 1fr)); gap: 10px; }
.ap-kpis article { display: grid; gap: 2px; padding: 11px 13px; border: 1px solid var(--line); border-left: 3px solid var(--blue); background: var(--white); }
.ap-kpis article.risk { border-left-color: var(--risk); }
.ap-kpis article.warning { border-left-color: var(--warning-graphic); }
.ap-kpis b { font: 700 24px var(--display); }
.ap-kpis article.risk b { color: var(--risk); }
.ap-kpis article.warning b { color: var(--warning); }
.ap-kpis span { color: var(--muted); font-size: 12px; }

.ap-charts-fold > summary { padding: 4px 0; color: var(--blue); font-size: 12px; font-weight: 700; cursor: pointer; }
.ap-charts-fold[open] > summary { margin-bottom: 8px; }
.ap-charts { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; }
.ap-charts article { padding: 12px 14px; border: 1px solid var(--line); background: var(--white); }
.ap-charts h4 { margin: 0 0 6px; font-size: 13px; }
.ap-note { margin: 0 0 8px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.ap-charts ul { display: grid; gap: 5px; margin: 0; padding: 0; list-style: none; }
.ap-charts button { width: 100%; display: grid; grid-template-columns: minmax(84px, 130px) 1fr 34px; gap: 8px; align-items: center; padding: 3px 4px; border: 0; background: transparent; font: inherit; text-align: left; cursor: pointer; }
.ap-charts button:hover, .ap-charts button:focus-visible { background: #EEF4FA; }
.ap-label { overflow: hidden; color: var(--ink); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.ap-track { height: 14px; background: var(--paper); }
.ap-track i { display: block; height: 100%; min-width: 2px; background: var(--blue); }
.ap-track i.risk { background: var(--risk); }
.ap-track i.warning { background: var(--warning-graphic); }
.ap-track i.normal { background: var(--green); }
.ap-charts b { font-size: 12px; text-align: right; }
</style>
